import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  styled,
  Table,
  TableBody,
  TableContainer,
  Tooltip,
} from '@mui/material';
import type { FC } from 'react';
import { useRef } from 'react';

import AuditTrail from '@/assets/icons/actions/audit-trail.svg';
import EditIcon from '@/assets/icons/actions/edit.svg';
import ViewIcon from '@/assets/icons/actions/view.svg';
import { PaginatedTableFooter } from '@/components/index';
// import { NoResultsAlert } from '@/components/notification/NoResultsAlert'
import {
  VirtualCell,
} from '@/components/table/StyledTableComponents';
import { TableHeader } from '@/components/table/TableHeader';
import { useBmProcessedVisualizedTable } from '@/hooks/shared/useBmProcessedVisualizedTable';
import type { TableColumn, TicketsData } from '@/utilities/types/table';
import type { SortableTableProps } from '@/utilities/types/table-props';

export const StyledRow = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  padding: '16px',
  height: '52px',
  '&:nth-of-type(even)': {
    backgroundColor: '#f5f5f5',
  },
  '&:nth-of-type(odd)': {
    backgroundColor: 'white',
  },
}))

export const StyledCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  wordBreak: 'break-word',
  textAlign: 'center',
  fontSize: '12px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}))

const columns: TableColumn[] = [
  { id: 'ticketNo', label: 'Ticket Number', width: '17.5%', sortable: true },
  // { id: 'areaName', label: 'Area', width: '10%', sortable: true },
  // { id: 'dealerName', label: 'Dealer', width: '10%', sortable: true },
  // { id: 'branch_name', label: 'Branch', sortable: false, width: '7.5%' },

  {
    id: 'typeOfConcern',
    label: 'Type of Concern',
    width: '10%',
    sortable: true,
  },

  {
    id: 'methodOfRepair',
    label: 'Method of Repair',
    sortable: true,
    width: '10%',
  },
  { id: 'dateCreated', label: 'Date Created', width: '10%', sortable: true },
  { id: 'dateModified', label: 'Date Modified', width: '10%', sortable: true },
  { id: 'ticketAgeInDays', label: 'Ticket Age', width: '10%', sortable: true },
  {
    id: 'currentLayer',
    label: 'Current Assignee Next Assignee',
    sortable: false,
    width: '12.5%',
  },
  { id: 'statusName', label: 'Status', width: '10%', sortable: true },
  { id: 'action', label: 'Action', width: '10%', align: 'center' },
]

type ProcessedTableProps = SortableTableProps<TicketsData>

export const MaintenanceTicketsBmProcessedTable: FC<ProcessedTableProps> = ({
  data = [],
  filterState,
  isLoading,
  error,
  onSort,
  role,
  handleDisplayAudit,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalElem,
  handleDisplay,
  handleUpdateModal,
  handleSort,
}) => {
  const parentRef = useRef<HTMLDivElement>(null)
  // const [showNoResults, setShowNoResults] = useState(false)

  const {

    paginatedItems,
    handlePageChange,
    handleRowsPerPageChange,
    handleVirtualizedScroll,
  } = useBmProcessedVisualizedTable({
    data,
    filterState,
    containerRef: parentRef,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalElem,
  })

  // useEffect(() => {
  //   setShowNoResults(data.length === 0 && isLoading)
  // }, [data.length, isLoading])

  const getStatusStyle = (row: TicketsData) => {
    const { status, workFlowId, currentLayerId } = row

    const statusMap: Record<string, { label: string; color: string }> = {
      ON_HOLD: { label: 'ON HOLD', color: '#21A0B3' },
      PENDING: { label: 'FOR APPROVAL', color: '#FFB12F' },
      FOR_APPROVAL: { label: 'FOR APPROVAL', color: '#FFB12F' },

      FOR_REPLY_LETTER_QUOTATION_UPLOAD: {
        label: 'FOR REPLY LETTER UPLOAD',
        color: '#6743EE',
      },
      APPROVED: { label: 'APPROVED', color: '#379F86' },
      FOR_DEALERS_APPROVAL: { label: 'FOR DEALERS APPROVAL', color: '#6743EE' },
      FOR_REVISION: { label: 'FOR REVISION', color: '#FFB12F' },
      FOR_COC_UPLOADING: { label: 'FOR COC', color: '#6743EE' },
      PENDING_COC: { label: 'PENDING COC', color: '#6743EE' },
      CLOSED: { label: 'CLOSED', color: '#444862' },
      REJECTED: { label: 'REJECTED', color: '#E74A59' },
      FOR_CONTRACTOR_QUOTATION_UPLOADING: {
        label: 'FOR CONTRACTOR QUOTATION',
        color: '#6743EE',
      },
      FOR_PO_QUOTATION_UPLOADING: {
        label: 'FOR PO RELEASE',
        color: '#6743EE',
      },
      FOR_COC_DATE_AND_COMPLETION_DATE_INPUT: {
        label: 'FOR COC DATE COMPLETION',
        color: '#21A0B3',
      },
      FOR_SCHEDULE: {
        label: 'FOR SCHEDULE',
        color: '#6743EE',
      },
    }

    const label = statusMap[status]?.label || status || ''
    const statusName = String(row.statusName ?? '')
    const isRevision =
      status === 'FOR_REVISION' ||
      label.toLowerCase().includes('revision') ||
      statusName.toLowerCase().includes('revision')
    const words = label.split(' ')
    const shortenedLabel =
      words.length > 2 ? `${words.slice(0, 2).join(' ')}...` : label

    return {
      label: shortenedLabel,
      color: statusMap[status]?.color || 'transparent',
      fullLabel: label,
      textColor: isRevision ? '#BF092F' : 'white',
    }
  }

  const isRevisionRow = (row: TicketsData) => {
    const status = String(row.status ?? '')
    const statusName = String(row.statusName ?? '')
    return (
      status.toLowerCase().includes('revision') ||
      statusName.toLowerCase().includes('revision')
    )
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          marginTop: '5px',
          height: '70vh',
          width: '100%',
          background: 'white',
          zIndex: 1,
          display: 'fleex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        Error: {error instanceof Error ? error.message : 'Unknown error'}
      </Box>
    )
  }

  return (
    <div
      style={{
        marginTop: '10px',
        boxShadow: 'none',
        width: '100%',
      }}
    >
      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 650, minHeight: '500px' }}
          aria-label="simple table"
        >
          <TableHeader
            columns={columns}
            filterState={filterState}
            onSort={onSort}
            handleSort={handleSort}
          />
          <TableBody
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            {paginatedItems?.length > 0 ? (
              paginatedItems?.map((row, index) => {
                const isRevision = isRevisionRow(row)

                return (
                  <StyledRow
                    key={index}
                    sx={{ color: isRevision ? '#E74A59' : 'inherit' }}
                  >
                    {columns.map((column) => {
                    if (column.id === 'ticketNo') {
                      return (
                        <StyledCell style={{ width: column.width }}>
                          {row.ticketNo || '-'}
                        </StyledCell>
                      )
                    }
                    // if (column.id === 'areaName') {
                    //   return (
                    //     <StyledCell width={column.width}>
                    //       {row.areaName || '-'}
                    //     </StyledCell>
                    //   )
                    // }
                    if (column.id === 'statusName') {
                      const { label, color, fullLabel, textColor } =
                        getStatusStyle(row)

                      return (
                        <StyledCell
                          key={`${column.id}-${row.ticketNo}`}
                          width={column.width}
                        >
                          <Tooltip title={fullLabel} arrow>
                            <Box
                              sx={{
                                backgroundColor: color,
                                color: textColor,
                                padding: '4px 8px',
                                borderRadius: '4px',
                                textAlign: 'center',
                                width: '120px',
                                fontFamily: 'Gabarito',
                                fontWeight: 500,
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                              }}
                            >
                              {label}
                            </Box>
                          </Tooltip>
                        </StyledCell>
                      )
                    }
                    if (column.id === 'dealerName') {
                      return (
                        <StyledCell width={column.width}>
                          {row.dealerName || '-'}
                        </StyledCell>
                      )
                    }
                    if (column.id === 'branch_name') {
                      return (
                        <StyledCell width={column.width}>
                          {row.branchName || '-'}
                        </StyledCell>
                      )
                    }
                    if (column.id === 'methodOfRepair') {
                      return (
                        <StyledCell width={column.width}>
                          {row.methodOfRepair.replace(/^C\/O/, 'c/o') || '-'}
                        </StyledCell>
                      )
                    }

                    if (column.id === 'dateCreated') {
                      return (
                        <StyledCell
                          width={column.width}
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          <span>
                            {new Date(row.dateCreated).toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: '2-digit',
                                year: 'numeric',
                              },
                            )}
                          </span>
                          <span>
                            {new Date(row.dateCreated).toLocaleTimeString(
                              'en-US',
                              {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                              },
                            )}
                          </span>
                        </StyledCell>
                      )
                    }

                    if (column.id === 'dateModified') {
                      return (
                        <StyledCell
                          width={column.width}
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          <span>
                            {new Date(row.dateModified).toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: '2-digit',
                                year: 'numeric',
                              },
                            )}
                          </span>
                          <span>
                            {new Date(row.dateModified).toLocaleTimeString(
                              'en-US',
                              {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                              },
                            )}
                          </span>
                        </StyledCell>
                      )
                    }
                    if (column.id === 'ticketAgeInDays') {
                      return (
                        <StyledCell width={column.width}>
                          {row.ticketAgeInDays || '-'}
                        </StyledCell>
                      )
                    }
                    if (column.id === 'typeOfConcern') {
                      return (
                        <StyledCell width={column.width}>
                          {row.typeOfConcern || '-'}
                        </StyledCell>
                      )
                    }

                    if (column.id === 'currentLayer') {
                      const left = row.aliasAssignedTo || 'END'
                      const right = row.aliasAssignedNext || 'END'

                      const bothEnd = left === 'END' && right === 'END'

                      return (
                        <Tooltip
                          title={
                            bothEnd
                              ? 'END'
                              : `${row.assignedTo || 'END'} → ${row.assignedNext || 'END'}`
                          }
                        >
                          <StyledCell
                            width={column.width}
                            sx={{
                              cursor: 'default',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              padding: '12px 16px',
                            }}
                          >
                            {bothEnd ? (
                              <Box textAlign="center" width="100%">
                                END
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr auto 1fr',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  width: '100%',
                                  textAlign: 'center',
                                }}
                              >
                                <Box>{left}</Box>
                                <ArrowRightAltIcon
                                  sx={{
                                    fontSize: '1rem',
                                    color: 'text.secondary',
                                  }}
                                />
                                <Box>{right}</Box>
                              </Box>
                            )}
                          </StyledCell>
                        </Tooltip>
                      )
                    }
         if (column.id === 'action') {
    const canAct = role === row.assignedTo
    return (
      <VirtualCell
        key={`${column.id}-${row.ticketNo ?? row.id}`}
        width={column.width}
      >
        {/* Always Show View */}
        <IconButton
          sx={{
            padding: '4px',
            '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
          }}
          onClick={() => handleDisplay?.(row)}
        >
          <img src={ViewIcon} alt="View" width={16} height={16} />
        </IconButton>

        {canAct ? (
          <>
            {/* Edit Button */}
            {[
              'Area Controller',
              'Maintenance Engineer',
              'Maintenance Admin',
            ].includes(role ?? '') &&
              ![
                'CLOSED',
                'DENIED',
                'REJECTED',
                'FOR_COC_UPLOADING',
                'FOR_SCHEDULE',
                'FOR_COC_DATE_AND_COMPLETION_DATE_INPUT',
              ].includes(row.status) && (
                <IconButton
                  sx={{
                    padding: '4px',
                    '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                  }}
                  onClick={() => handleUpdateModal?.(row)}
                >
                  <img src={EditIcon} alt="Edit" width={16} height={16} />
                </IconButton>
              )}

            {/* Approve Button */}
            {!['CLOSED', 'DENIED', 'REJECTED'].includes(row.status) &&
              !['DND Purchasing Admin', 'GA Purchasing Staff'].includes(role ?? '') && (
                <IconButton
                  sx={{
                    padding: '4px',
                    '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                  }}
                  onClick={() => {
                    if (
                      (row.currentLayerId === 4 &&
                        role === 'Maintenance Admin' &&
                        row.workFlowId === 5) ||
                      (row.currentLayerId === 8 &&
                        role === 'Maintenance Admin' &&
                        row.workFlowId === 4)
                    ) {
                      handleShowApproveDIS(row)
                    } else if (
                      row.currentLayerId === 10 &&
                      row.workFlowId === 5 &&
                      role === 'Maintenance Admin'
                    ) {
                      handleShowApproveDISWithGatePass(row)
                    } else {
                      handleShowApprove(row)
                    }
                  }}
                >
                  <img src={ApproveIcon} alt="Approve" width={16} height={16} />
                </IconButton>
              )}

            {/* Deny Button */}
            {!['CLOSED', 'DENIED', 'REJECTED'].includes(row.status) && (
              <IconButton
                sx={{
                  padding: '4px',
                  '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                }}
                onClick={() => handleShowDeny(row)}
              >
                <img src={DenyIcon} alt="Deny" width={16} height={16} />
              </IconButton>
            )}
          </>
        ) : (
          // Audit Trail (when user can't act)
          <IconButton
            sx={{
              padding: '4px',
              '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
            }}
            onClick={() => {
              if (handleDisplayAudit) {
                handleDisplayAudit(row.id)
              }
            }}
          >
            <img src={AuditTrail} alt="Audit" width={16} height={16} />
          </IconButton>
        )}
                        </VirtualCell>
                      )
                    }
                    })}
                  </StyledRow>
                )
              })
            ) : (
              <StyledRow>
                <StyledCell
                  sx={{
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'flex',
                    color: '#888',
                    fontSize: '14px',
                    fontStyle: 'italic',
                    height: '52px',
                  }}
                >
                  No tickets found.
                </StyledCell>
              </StyledRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PaginatedTableFooter
        rowsPerPage={rowsPerPage}
        totalCount={totalElem}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  )
}
